/** Tamanho alvo para upload (3 MB — equilíbrio entre qualidade e confiabilidade no mobile). */
export const VEHICLE_IMAGE_TARGET_MAX_BYTES = 3 * 1024 * 1024;

/** Só otimiza arquivos acima deste tamanho (HEIC sempre passa pela conversão). */
export const VEHICLE_IMAGE_OPTIMIZE_ABOVE_BYTES = 1.5 * 1024 * 1024;

/** Limite absoluto antes de rejeitar (10 MB). */
export const VEHICLE_IMAGE_HARD_MAX_BYTES = 10 * 1024 * 1024;

/** Lado maior máximo — preserva qualidade em telas grandes. */
export const VEHICLE_IMAGE_MAX_SIDE = 2560;

const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

type ImageSource = HTMLImageElement | ImageBitmap;

function isHeicFile(file: File): boolean {
  const type = (file.type || '').toLowerCase();
  if (type.includes('heic') || type.includes('heif')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext ? HEIC_EXTENSIONS.has(ext) : false;
}

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  if (/\.(jpe?g|png|webp|gif|bmp|heic|heif)$/i.test(file.name)) return true;
  if (!file.type && file.size > 0) return true;
  return false;
}

function hasSafeFileName(name: string): boolean {
  return Boolean(name) && name.length < 120 && !/[^\x20-\x7E]/.test(name);
}

function sanitizeUploadFileName(file: File, preferredExt = 'jpg'): File {
  if (hasSafeFileName(file.name)) return file;
  return new File([file], `veiculo-${Date.now()}.${preferredExt}`, {
    type: file.type || `image/${preferredExt === 'jpg' ? 'jpeg' : preferredExt}`,
    lastModified: file.lastModified || Date.now(),
  });
}

function getSourceDimensions(source: ImageSource): { w: number; h: number } {
  if (source instanceof ImageBitmap) {
    return { w: source.width, h: source.height };
  }
  return { w: source.naturalWidth, h: source.naturalHeight };
}

function releaseImageSource(source: ImageSource): void {
  if (source instanceof ImageBitmap) {
    source.close();
  }
}

/** Decodifica sem redimensionar — resizeWidth + resizeHeight iguais distorcem a proporção. */
async function loadImageBitmap(file: Blob): Promise<ImageBitmap | null> {
  if (typeof createImageBitmap !== 'function') return null;
  try {
    return await createImageBitmap(file);
  } catch {
    return null;
  }
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const blobUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Não foi possível carregar a imagem para otimização.'));
    };
    img.src = blobUrl;
  });
}

async function loadImageSource(file: File): Promise<ImageSource> {
  const bitmap = await loadImageBitmap(file);
  if (bitmap) return bitmap;
  return loadImageFromBlob(file);
}

/** Calcula dimensões de saída mantendo proporção original. */
function fitInsideBox(srcW: number, srcH: number, maxSide: number): { w: number; h: number } {
  if (!srcW || !srcH) {
    throw new Error('Não foi possível ler dimensões da imagem.');
  }
  if (srcW <= maxSide && srcH <= maxSide) {
    return { w: srcW, h: srcH };
  }
  if (srcW >= srcH) {
    return { w: maxSide, h: Math.round((srcH * maxSide) / srcW) };
  }
  return { w: Math.round((srcW * maxSide) / srcH), h: maxSide };
}

function renderSourceToCanvas(source: ImageSource, maxSide: number): HTMLCanvasElement {
  const { w: srcW, h: srcH } = getSourceDimensions(source);
  const { w, h } = fitInsideBox(srcW, srcH, maxSide);

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas não disponível neste navegador.');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(source, 0, 0, srcW, srcH, 0, 0, w, h);
  return canvas;
}

function canvasToJpegBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Falha ao gerar JPEG otimizado.'));
      },
      'image/jpeg',
      quality,
    );
  });
}

function buildJpegFile(blob: Blob, originalName: string): File {
  const baseName = originalName.replace(/\.[^.]+$/, '') || 'veiculo';
  const safeBase = hasSafeFileName(baseName) ? baseName : `veiculo-${Date.now()}`;
  return new File([blob], `${safeBase}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

async function compressToTargetJpeg(
  file: File,
  maxSide: number,
  targetBytes: number,
  hardMaxBytes: number,
): Promise<File> {
  const source = await loadImageSource(file);

  try {
    const maxSides = [maxSide, 2048, 1920, 1600, 1280].filter(
      (side, index, arr) => arr.indexOf(side) === index,
    );
    const qualities = [0.92, 0.88, 0.84, 0.8, 0.76, 0.72, 0.68, 0.64];

    let smallestBlob: Blob | null = null;

    for (const side of maxSides) {
      const canvas = renderSourceToCanvas(source, side);

      for (const quality of qualities) {
        const blob = await canvasToJpegBlob(canvas, quality);
        if (!smallestBlob || blob.size < smallestBlob.size) {
          smallestBlob = blob;
        }
        if (blob.size <= targetBytes) {
          return buildJpegFile(blob, file.name);
        }
      }
    }

    if (smallestBlob && smallestBlob.size <= hardMaxBytes) {
      return buildJpegFile(smallestBlob, file.name);
    }

    throw new Error(
      'Não foi possível reduzir a imagem para um tamanho aceitável. Tente outra foto ou reduza a resolução no celular.',
    );
  } finally {
    releaseImageSource(source);
  }
}

function shouldOptimizeImage(file: File): boolean {
  if (isHeicFile(file)) return true;
  if (file.size > VEHICLE_IMAGE_OPTIMIZE_ABOVE_BYTES) return true;
  return false;
}

/**
 * Prepara fotos de veículo para upload.
 * Só redimensiona/comprime quando necessário; sempre preserva a proporção original.
 */
export async function prepareVehicleImageForUpload(file: File): Promise<File> {
  if (!isImageFile(file)) return sanitizeUploadFileName(file);

  if (!shouldOptimizeImage(file)) {
    return sanitizeUploadFileName(file);
  }

  try {
    return await compressToTargetJpeg(
      file,
      VEHICLE_IMAGE_MAX_SIDE,
      VEHICLE_IMAGE_TARGET_MAX_BYTES,
      VEHICLE_IMAGE_HARD_MAX_BYTES,
    );
  } catch (err) {
    if (isHeicFile(file)) {
      return sanitizeUploadFileName(file, 'heic');
    }
    throw err;
  }
}

/** @deprecated Use prepareVehicleImageForUpload */
export async function compressImageIfNeeded(
  file: File,
  maxBytes = VEHICLE_IMAGE_HARD_MAX_BYTES,
): Promise<File> {
  return prepareVehicleImageForUpload(file);
}

export { isHeicFile, isImageFile };
